import React, { FC as FCReact, useMemo } from 'react';
import { buildQaIdProp } from 'utils/build-qa-id';
import { convertToArborDate } from 'models/time/arbor-date';
import Communication from 'components/note/communication';
import Intervention from 'components/note/intervention';
import Incident from 'components/note/incident';
import ListIcon from '@mui/icons-material/List';
import {
  DC,
  FC,
  CSL,
  FA,
  PA,
  QRE,
  AR,
  RS,
  DUR,
  ORDER,
  FC_ORDER,
  THERAPY_MED_SYNC,
} from 'constants/index';
import moment from 'moment';
import { ReactComponent as InterventionIcon } from 'lib/logos/intervention.svg';
import { ReactComponent as IncidentIcon } from 'lib/logos/incident.svg';
import { counselingTaskTypes, fcInProgressReasons, noteTagTypes } from 'constants/lists';
import { NoteTagTypes } from 'constants/note-tag-types';
import { getDisplayNotes } from 'services/utils/note-service';
import { findMatchingDocuments } from 'services/utils/document-service';
import {
  CAIcon,
  DSIcon,
  CommunicationIcon,
  PerProtocolIcon,
  PRNIcon,
  APIcon,
  getCMIcon,
  MedicarePartBIcon,
} from 'components/icons/icons';
import DisplayOrderForm from 'containers/shipping/display-order-form';
import { TaskCategory } from 'interfaces/enums/TaskCategory';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { getStatusNameByStatusId } from 'services/utils/task-service';
import { integrateUpdatedTasks } from 'actions/action-tasks';
import {
  TableCell,
  TableRow,
  Typography,
  Checkbox,
  Box,
  IconButton,
  Tooltip,
  Grid,
  Divider,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { ITherapy } from 'interfaces/redux/ITherapy';
import AddIcon from '@mui/icons-material/Add';
import { updateDocumentBar } from 'actions/action-uploaded-documents';
import { openCommunicationNotesSideBar } from 'actions/action-notes';
import { NoteBarTrigger, TaskTypesEnum } from 'constants/enums';
import { useFillCycleNumber } from 'hooks/useFillCycleNumber';

import { FillCoordinationStatus } from 'interfaces/enums/TaskStatuses/FillCoordinationStatus';
import { MedSyncTooltipIcon } from 'containers/patient/med-sync/components/medSyncIcon';
import { MedicationsTooltip } from 'containers/tasks/fill-coordination/components/medications-tooltip';
import { windowFeatureIsEnabled } from 'config/window-features';
import { selectPatientId, selectPatientName } from 'hooks/usePatient';
import { useDeepDiffSelector } from 'utils/deep-diff-props';
import { FillDeliveryConfirmationStatus } from 'interfaces/enums/TaskStatuses/FillDeliveryConfirmationStatus';
import { SecureFCClient } from '../../../../clients/secure-fc-client';
import { notifyError } from '../../../../actions/action-notifications';
import { NeedsByDateDialogForm } from '../forms/needsby-date-task-therapy';
import {
  NoteTooltip,
  NoteTooltipContent,
  NoteContent,
  DosingInformationTooltip,
  DosingInformationTooltipContent,
} from './tooltips';
import { DosingPillIcon } from '../icons';
import { styles } from './table.styles';
import { ITasksTableRowProps, IDiagnosesObj } from './interfaces';
import {
  FollowUpDateDialogForm as CompletedDateDialogForm,
  FollowUpDateDialogForm,
} from '../forms/followup-date-task-form';
import { MedSyncDisplayStatus } from '../../med-sync/common';
import { getIsTaskKeySelected } from '../selectors';

const emptyDateLabel = '-';

const wasPartOfAPriorBundle = (task: any) => task?.taskType === FC && task.secure_link_id < 0;

const qaIdProp = buildQaIdProp('tasks-table');

// eslint-disable-next-line react/function-component-definition
const TasksTableRow: FCReact<ITasksTableRowProps> = (props): JSX.Element => {
  const {
    classes,
    taskKey,
    index,
    onSelectedRow,
    disableUpdateDates,
    updatedTasks,
    setTriggerRowUpdate,
    version,
    isChecked,
    onTaskTypeClicked,
  } = props;
  const dispatch = useDispatch();
  const isTaskSelected = useTypedSelector(
    state => isChecked || (version !== 'archived' && getIsTaskKeySelected(state, taskKey)),
  );
  const tasks = Object.values(useTypedSelector(state => state.tasks.data));
  const task = useTypedSelector(state => state.tasks.data[taskKey] ?? {});
  const therapies = useTypedSelector(state => state.therapies.data);
  const serviceGroups = useTypedSelector(state => state.lookups.serviceGroups);
  const lookups = useTypedSelector(state => state.lookups);
  const tasksStatuses = useTypedSelector(state => state.taskStatuses.statuses);
  const tabControl = useTypedSelector(state => state.tabControl);

  const { users } = lookups;
  const { allPinnedNotes, allNonPinnedNotes, allNotes } = useDeepDiffSelector(state => {
    const noteState = state.notes?.notes ?? {
      allPinnedNotes: [],
      allNonPinnedNotes: [],
      allNotes: [],
    };
    return {
      allPinnedNotes: noteState.allPinnedNotes,
      allNonPinnedNotes: noteState.allNonPinnedNotes,
      allNotes: noteState.allNotes,
    };
  });
  const uploadedDocuments = useTypedSelector(state => state.uploadedDocuments);
  const clinicalDataItems = useTypedSelector(state => state.clinicalData);
  const documents = uploadedDocuments.documents || [];
  const patientId = useSelector(selectPatientId);
  const { patientFullName } = useSelector(selectPatientName);
  const therapy: ITherapy = {
    ...(therapies?.[task.therapy_id] ||
      therapies?.[task.therapyInitialId as unknown as number] ||
      therapies?.[task.therapy_ids?.split(',')[0] as unknown as number] ||
      {}),
  };
  const order_therapies: ITherapy[] = (task.therapy_ids || '')
    .split(',')
    .map(x => therapies?.[Number(x)])
    .filter((t): t is ITherapy => !!t);

  if (windowFeatureIsEnabled('fc_orders_task_table') && task.taskType === 'Order') {
    // for taskType 'Order' artificially update the therapy as a summary of all order therapies for displaying badges like CA, OTF, etc.
    therapy.ids = task.therapy_ids?.split(',').map((id: any) => Number(id));
    therapy.id = Number(task.therapy_ids?.split(',')[0]) as unknown as number;
    therapy.patient_id = therapies?.[therapy.id]?.patient_id;
    therapy.clinic_administered = (order_therapies || []).some(t => t.clinic_administered);
    therapy.as_needed = (order_therapies || []).some(t => t.as_needed);
    therapy.one_time_fill = (order_therapies || []).some(t => t.one_time_fill);
    therapy.drop_ship_med = (order_therapies || []).some(t => t.drop_ship_med);
    therapy.medicare_part_b = (order_therapies || []).some(t => t.medicare_part_b);
    therapy.controlled_substance_code = (order_therapies || []).find(
      t => !!t.controlled_substance_code,
    )?.controlled_substance_code;
    therapy.adherence_packaging = (order_therapies || []).find(
      t => t.adherence_packaging,
    )?.adherence_packaging;
    therapy.in_med_sync = (order_therapies || []).find(
      t => t.in_med_sync === THERAPY_MED_SYNC.IN_MED_SYNC,
    )?.in_med_sync;
    therapy.dosis_regimen = (order_therapies || []).find(t => t.dosis_regimen)?.dosis_regimen;
  }

  const tagType = noteTagTypes.find(tag => tag.label === task.taskType);
  const tagTypeId = tagType ? tagType.value : null;
  const contactTagTypeId = task.taskType === TaskTypesEnum.ORDER ? NoteTagTypes.FC : tagTypeId;
  const contactTagResourceId =
    task.taskType === TaskTypesEnum.ORDER ? patientId || task.id : task.id;
  const taskStatus =
    task?.task_status_text || getStatusNameByStatusId(task.status_id, tasksStatuses);

  const addFCToBundle = async (task: any) => {
    const payload = {
      fc_task_id: task.id,
    };
    try {
      const response = await SecureFCClient.addFCBackToBundle(payload);
      dispatch(integrateUpdatedTasks(response.data.updatedTasks));
      const newTask = response.data.updatedTasks.find(
        (task: any) => task.id === payload.fc_task_id,
      );
      setTimeout(() => onSelectedRow(newTask), 1);
    } catch (error) {
      dispatch(notifyError('Error adding the FC to the bundle'));
      console.log('Error adding the FC to the bundle: ', error);
    }
  };

  const displayNotes = useMemo(() => {
    if (task.taskType === 'Order') {
      const combined_notes = [];
      for (const fc_id of task.fc_ids?.split(',') || []) {
        const therapyIdFromTask = tasks.find(task => task.id === Number(fc_id))?.therapy_id;
        const x = getDisplayNotes(
          allPinnedNotes,
          allNonPinnedNotes,
          15,
          Number(fc_id),
          therapyIdFromTask,
          tasks,
          therapies,
          users,
          serviceGroups,
          2,
        );
        combined_notes.push(...x);
      }
      return combined_notes;
    }
    return getDisplayNotes(
      allPinnedNotes,
      allNonPinnedNotes,
      tagTypeId,
      task.id,
      task.therapy_id,
      tasks,
      therapies,
      users,
      serviceGroups,
      2,
    );
  }, [allNonPinnedNotes, allPinnedNotes, serviceGroups, tagTypeId, task.id, users, tabControl]);

  const allNotesForTask = useMemo(
    () =>
      allNotes.filter((n: any) =>
        n.tags.some((t: any) => t?.tag_type_id === tagTypeId && t?.resource_id === task.id),
      ),
    [allNotes, tagTypeId, task.id],
  );

  const taskDocuments =
    findMatchingDocuments(documents, [{ tagTypeId: tagTypeId, resourceId: task.id }]) || [];

  const handleOnShowDocuments = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const resource_ids = task.fc_ids
      ? task.fc_ids.split(',').map((id: any) => Number(id))
      : [task.id];
    const order_fc_ids = task.taskType === 'Order' ? resource_ids : [];
    const order_therapy_ids =
      task.taskType === 'Order' && task.therapy_ids
        ? task.therapy_ids.split(',').map((id: any) => Number(id))
        : [];
    const tag_type_id = task.taskType === 'Order' ? NoteTagTypes.FC : tagTypeId;
    dispatch(
      updateDocumentBar({
        display: true,
        taskIdResourceIds: resource_ids.map((id: any) => ({
          tagTypeId: tag_type_id,
          resourceId: id,
        })),
        tagTypeId: tag_type_id,
        resourceId: task?.id,
        drugName: therapy?.drug_name || '',
        tagTypeLabel: task?.type,
        update: false,
        order_fc_ids,
        therapy_ids: order_therapy_ids,
        selected_therapies: [{ value: -1, label: 'All Therapies in the Order' }],
      }),
    );
  };

  const handleOnShowNotes = () => {
    const task_id = task.taskType === 'Order' ? task.fc_ids?.split(',')?.map(Number)[0] : task.id;
    const multi_fc_order =
      task.taskType === 'Order' && task.fc_ids && task.fc_ids?.split(',')?.length > 1;
    const payload = {
      noteBar: {
        display: true,
        tagTypeId: task.taskType === 'Order' ? 15 : tagType?.value,
        tagResourceId: task_id,
        trigger: NoteBarTrigger.Notes,
        tag: task.taskType !== 'Order' ? task.taskType : 'FC',
        showAllNotes: false,
        selected_therapies: [{ value: -1, label: 'All Therapies in the Order' }],
        ...(multi_fc_order ? { tagResourceIds: task.fc_ids?.split(',')?.map(Number) } : {}),
        ...(multi_fc_order ? { order_fc_ids: task?.fc_ids?.split(',')?.map(Number) } : {}),
        ...(multi_fc_order ? { therapy_ids: task.therapy_ids?.split(',')?.map(Number) } : {}),
      },
      therapy,
    };

    dispatch(openCommunicationNotesSideBar(payload));
  };

  const taskIsOverdue = convertToArborDate(task.followup_dt, true).isBeforeToday();
  const taskIsComplete =
    task.status_category_id === TaskCategory.Done ||
    task.status_category_id === TaskCategory.Archived;
  let rowStyling = index % 2 === 0 ? classes.evenRow : classes.oddRow;

  if (taskIsComplete) {
    rowStyling = classes.completedRow;
  }
  if (isTaskSelected) {
    rowStyling = classes.highlightRow;
  }

  let taskLabelStyling = classes.inProgressText;
  if (taskIsOverdue) {
    taskLabelStyling = classes.outstandingText;
  }

  const taskIsLinkedToTherapy =
    Boolean(task.therapy_id || task.therapy_ids) && task.taskType !== AR && task.taskType !== DUR;
  let arrTherapyFullNames = ['-'];
  let therapiesIdArrForTask: string[] = [];
  if (taskIsLinkedToTherapy) {
    if ((task && task.therapy_ids) || (therapy && therapy.id)) {
      therapiesIdArrForTask = (task.therapy_ids || `${therapy ? therapy.id : ''}`)
        .split(',')
        .filter((id: any) => (therapies || {})[Number(id)]);
      arrTherapyFullNames = therapiesIdArrForTask.map(id => {
        const therapyInstance = therapies[Number(id)];
        const drugName = therapyInstance?.drug_name;
        const therapyDosage =
          therapyInstance && therapyInstance.strength && therapyInstance.strength_unit_of_measure
            ? `- ${therapyInstance.strength} ${therapyInstance.strength_unit_of_measure}`
            : '';
        return `${drugName || ''}${therapyDosage ? ` ${therapyDosage}` : ''}`;
      });
    }
  } else if (task.taskType === AR) {
    const arDrugs = task.drugs;
    arrTherapyFullNames = arDrugs?.map(
      (drug: any) => `${drug.drug_name} ${drug.strength} - ${drug.specialty_type}`,
    ) || ['-'];
  }
  const baseTherapyFullName = arrTherapyFullNames[0];
  const therapiesForTooltip = arrTherapyFullNames.slice(1).join(', ') || null;

  const taskStatusStyle = taskIsComplete ? classes.completedText : undefined;
  const followupDate = task?.followup_dt
    ? convertToArborDate(task.followup_dt, true).getUtcDate(true, 'M/D/YY')
    : emptyDateLabel;
  const completedDateField = task?.updated;
  const completedDate = completedDateField
    ? convertToArborDate(completedDateField).getCustomerDate(true, 'M/D/YY')
    : emptyDateLabel;
  const [needsByDateUTC, isPerProtocol] = useFillCycleNumber(
    task.therapy_id,
    task.fill_cycle_number,
  );
  const needsByDate = React.useMemo(
    () =>
      task?.taskType === 'FDC' || task?.taskType === 'Order'
        ? convertToArborDate(task?.due_date, true).getUtcDate(true, 'M/D/YY')
        : convertToArborDate(needsByDateUTC, true).getUtcDate(true, 'M/D/YY'),
    [needsByDateUTC, task?.due_date, task?.taskType],
  );
  const actionIconStyling = taskIsComplete
    ? classes.actionIconTaskInProgress
    : classes.actionIconTaskCompleted;
  const noteCount = allNotesForTask.length > 0 ? allNotesForTask.length : '-';
  const isInMedSync = therapy?.in_med_sync === THERAPY_MED_SYNC.IN_MED_SYNC;
  const showNBD =
    task.taskType === 'FC' ||
    task.taskType === 'Order' ||
    (task.taskType === 'FDC' && task.due_date != null);
  const dosingInformation: string = (therapy && therapy.dosis_regimen) || '';

  const getFillCycleNumberForTherapy = (therapyId?: number): number => {
    if (!therapyId) {
      return task.fill_cycle_number || 0;
    }

    if (task.taskType === 'Order') {
      const relatedFc = tasks.find(
        (t: any) =>
          t.taskType === FC &&
          Number(t.therapy_id) === Number(therapyId) &&
          task.fc_ids?.split(',').includes(String(t.id)),
      );
      return relatedFc?.fill_cycle_number || 0;
    }

    return task.fill_cycle_number || 0;
  };

  const pillTooltipTherapies = (task.taskType === 'Order' ? order_therapies : [therapy])
    .filter((t): t is ITherapy => Boolean(t?.id))
    .map((t: ITherapy) => ({
      drug_name: t.drug_name || '',
      strength: t.strength || '',
      strength_unit_of_measure: t.strength_unit_of_measure || '',
      fill_cycle_number: getFillCycleNumberForTherapy(t.id),
      administration_status: t.administration_status || '',
      diagnosis_code: t.diagnosis_code || '',
      diagnosis_description: t.diagnosis_description,
      start_date: t.start_date,
      last_csl_completed_dt: t.last_csl_completed_dt,
      prescribing_physician_name: t.prescribing_physician_name,
      prescribing_physician_npi: t.prescribing_physician_npi,
      prescribing_physician_credential: t.prescribing_physician_credential,
      dosis_regimen: t.dosis_regimen,
      days_supply: t.days_supply,
      therapy_id: t.id,
    }));
  const getTaskLabel = () => {
    let taskPostscript = '';
    switch (task.taskType) {
      case ORDER:
        return `${FC_ORDER}${
          arrTherapyFullNames.length > 1 ? ` (${arrTherapyFullNames.length})` : ''
        }`;
      case FC:
        if (windowFeatureIsEnabled('fc_orders_task_table')) {
          return 'Order';
        }
        if (task.fill_cycle_number) {
          taskPostscript = ` #${task.fill_cycle_number}`;
        }
        break;
      case CSL:
        const cslTaskTypeName = counselingTaskTypes.find(
          type => type.id === task.counseling_type,
        )?.name;
        if (cslTaskTypeName) {
          taskPostscript = ` - ${cslTaskTypeName}`;
        }
        break;
      case FA:
      case PA:
        taskPostscript = ` - ${task.is_renewal ? 'Renewal' : 'New'}`;
        break;
      case QRE:
        const categoryName = lookups.incidentCategories.find(
          item => item.id === task.category_id,
        )?.name;
        taskPostscript = categoryName ? ` - ${categoryName}` : '';
        break;
      default:
        break;
    }
    return `${task.taskType}${taskPostscript}`;
  };
  const dcWithProm = () => {
    if (![DC, RS].includes(task.taskType)) return false;

    const dcClinicalDataItems =
      clinicalDataItems?.filter(
        item => item.taskDataCollectId === task.id && !!item.questionnaire,
      ) || [];
    return dcClinicalDataItems.length > 0;
  };

  const therapyDiagnosisTooltip = () => {
    const therapiesToFormat = task.taskType === 'Order' ? order_therapies : [therapy];

    const formattedTherapies = therapiesToFormat
      .filter((t): t is ITherapy => Boolean(t?.id))
      .map((t: ITherapy, idx: number) => {
        const strength = t.strength ? `${t.strength}${t.strength_unit_of_measure}` : '';
        const fullName = `${t.drug_name}${strength ? ` ${strength}` : ''}`;
        const fcNumber = getFillCycleNumberForTherapy(t.id);
        const fcSuffix = fcNumber ? ` (FC# ${fcNumber})` : '';
        return { index: idx + 1, name: fullName, suffix: fcSuffix };
      });

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {formattedTherapies.map((item, idx) => (
          <Box key={`${item.name}-${idx}`}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
              }}
            >
              <Typography sx={{ fontWeight: 700, minWidth: '24px' }}>{item.index}.</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {item.name}
                {item.suffix}
              </Typography>
            </Box>
            {idx < formattedTherapies.length - 1 && (
              <Divider sx={{ my: 1.5, backgroundColor: '#fff' }} />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const showTherapyTooltip = therapy && ![AR, RS, DUR].includes(task.taskType);

  const showAddToPriorBundle = useMemo(() => wasPartOfAPriorBundle(task), [task]);

  React.useEffect(() => {
    if (updatedTasks?.some(t => t.id === task.id && t.taskType === task.taskType)) {
      setTriggerRowUpdate([]);
    }
  }, [setTriggerRowUpdate, task.id, task.taskType, updatedTasks]);
  return (
    <TableRow {...qaIdProp(`row-${task.type}-${task.id}`)} className={rowStyling}>
      <TableCell align="left" {...qaIdProp('select')}>
        <Checkbox
          color="primary"
          disableRipple
          checked={isTaskSelected}
          onClick={() => onSelectedRow(task)}
        />
      </TableCell>
      <TableCell align="left" {...qaIdProp('type')} className={classes.taskTypeCell}>
        {task?.taskType === 'FDC' && task?.status_id === FillDeliveryConfirmationStatus.Dispense ? (
          <div className={classes.taskTabTaskContainer}>
            <div onClick={onTaskTypeClicked} className={classes.tasktabLabelIcon}>
              <Typography className={taskLabelStyling}>
                {getTaskLabel()}
                {dcWithProm() && <ListIcon className={classes.dcPromIcon} />}
                {showAddToPriorBundle ? (
                  <Tooltip title="Add back to Secure Link Bundle">
                    <div className={classes.plusSignButton} onClick={() => addFCToBundle(task)}>
                      +
                    </div>
                  </Tooltip>
                ) : null}
              </Typography>
            </div>
            <div className={classes.tasktabLabelIcon}>
              <DisplayOrderForm data={task} patientId={patientId} />
            </div>
          </div>
        ) : (
          <>
            <div onClick={onTaskTypeClicked} className={classes.taskTabTaskContainer}>
              {task.taskType === 'Order' ? (
                <Typography className={taskLabelStyling}>
                  {getTaskLabel()}
                  {dcWithProm() && <ListIcon className={classes.dcPromIcon} />}
                  {showAddToPriorBundle ? (
                    <Tooltip title="Add back to Secure Link Bundle">
                      <div className={classes.plusSignButton} onClick={() => addFCToBundle(task)}>
                        +
                      </div>
                    </Tooltip>
                  ) : null}
                </Typography>
              ) : (
                <Typography className={taskLabelStyling}>
                  {getTaskLabel()}
                  {dcWithProm() && <ListIcon className={classes.dcPromIcon} />}
                  {task && task.taskType === 'FDC' ? (
                    <DisplayOrderForm data={task} patientId={patientId} />
                  ) : null}
                  {showAddToPriorBundle ? (
                    <Tooltip title="Add back to Secure Link Bundle">
                      <div className={classes.plusSignButton} onClick={() => addFCToBundle(task)}>
                        +
                      </div>
                    </Tooltip>
                  ) : null}
                </Typography>
              )}
            </div>
            {task.taskType === 'Order' && task.order_id ? <div>({task.order_id})</div> : null}
            {task.includes_fc_number_1 && !task.order_id ? (
              <div className={classes.orderIdText}>Includes FC#1</div>
            ) : null}
          </>
        )}
      </TableCell>
      <TableCell align="left" {...qaIdProp('therapy')}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography className={classes.therapyNameFull}>
            {therapy || task.therapy_ids ? (
              task.taskType === 'Order' ? (
                <Tooltip
                  title={therapyDiagnosisTooltip()}
                  placement="top"
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: true,
                        options: {
                          padding: 8,
                        },
                      },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          padding: 8,
                        },
                      },
                    ],
                  }}
                >
                  <span style={{ cursor: 'pointer' }}>{baseTherapyFullName}</span>
                </Tooltip>
              ) : (
                <span style={{ cursor: 'pointer' }}>{baseTherapyFullName}</span>
              )
            ) : (
              <span>{baseTherapyFullName}</span>
            )}
            {therapiesForTooltip &&
              (task.taskType === 'Order' ? (
                <Tooltip
                  title={therapiesForTooltip}
                  className={classes.therapyEllipsis}
                  placement="top"
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: true,
                        options: {
                          padding: 8,
                        },
                      },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          padding: 8,
                        },
                      },
                    ],
                  }}
                >
                  <span>...</span>
                </Tooltip>
              ) : (
                <span>...</span>
              ))}
            {Boolean(therapy?.one_time_fill) &&
            ((task.taskType === 'FC' && task.fill_cycle_number === 1) ||
              task.taskType === 'Order') ? (
              <Tooltip title="One-Time Fill">
                <Typography component="span" className={`${classes.therapyTag} OTF`}>
                  OTF
                </Typography>
              </Tooltip>
            ) : null}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!!therapy?.adherence_packaging && [FC, 'Order'].includes(task.taskType) ? (
              <Tooltip title="Adherence Package Required" placement="bottom-start">
                <span>
                  <APIcon className={classes.prnIcon} />
                </span>
              </Tooltip>
            ) : null}
            {windowFeatureIsEnabled('fc_orders_task_table') && task.taskType === 'Order' ? (
              <MedicationsTooltip
                medicationsList={pillTooltipTherapies}
                maxDisplayInTooltip={4}
                showIndex={false}
              >
                <DosingPillIcon
                  fontSize="small"
                  className={dosingInformation || classes.dosingIconDisabled}
                />
              </MedicationsTooltip>
            ) : (
              <DosingInformationTooltip
                title={
                  showTherapyTooltip ? (
                    <DosingInformationTooltipContent
                      therapies={order_therapies}
                      dosingInformation={{
                        ...therapy,
                        dosis_regimen: task.dosis_regimen || therapy.dosis_regimen,
                      }}
                    />
                  ) : (
                    ''
                  )
                }
              >
                <DosingPillIcon
                  fontSize="small"
                  className={dosingInformation || classes.dosingIconDisabled}
                />
              </DosingInformationTooltip>
            )}
            {windowFeatureIsEnabled('med_sync') && therapy?.in_med_sync ? (
              <MedSyncTooltipIcon
                displayStatus={MedSyncDisplayStatus.Synchronized}
                tooltipTitle="Med Sync"
                size="sm"
              />
            ) : null}
            {therapy?.as_needed && [FC, 'Order'].includes(task.taskType) ? (
              <PRNIcon className={classes.prnIcon} />
            ) : null}
            {therapy?.clinic_administered && [FC, 'Order'].includes(task.taskType) ? (
              <CAIcon className={classes.prnIcon} />
            ) : null}
            {therapy?.medicare_part_b && [FC, 'Order'].includes(task.taskType) ? (
              <Tooltip title="Medicare Part B">
                <MedicarePartBIcon fs="10px" w="15px" h="15px" mb="0px" />
              </Tooltip>
            ) : null}
            {therapy?.controlled_substance_code
              ? getCMIcon(therapy?.controlled_substance_code, classes.prnIcon)
              : null}
            {therapy?.drop_ship_med ? (
              <Tooltip title="Drop-Ship Med">
                <span>
                  <DSIcon className={classes.prnIcon} />
                </span>
              </Tooltip>
            ) : null}
            <Typography>{therapy?.administration_status}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell align="left" {...qaIdProp('status')}>
        {task.status_id === FillCoordinationStatus.In_Progress && task.in_progress_reason ? (
          <Tooltip
            title={
              <Grid padding={0.5}>
                <Typography className={taskStatusStyle}>Reason:</Typography>
                <Typography className={taskStatusStyle} fontWeight="bold">
                  {
                    fcInProgressReasons.find(
                      reason => reason.value === Number(task.in_progress_reason),
                    )?.label
                  }
                </Typography>
              </Grid>
            }
            placement="bottom-start"
          >
            <Typography className={taskStatusStyle}>{taskStatus}</Typography>
          </Tooltip>
        ) : (
          <Typography className={taskStatusStyle}>{taskStatus}</Typography>
        )}
      </TableCell>
      {version === 'active' ? (
        <TableCell align="left" {...qaIdProp('followup_dt')}>
          <Box display="flex" alignItems="center">
            {!disableUpdateDates && (
              <FollowUpDateDialogForm
                iconClassName={classes.dateIcon}
                tasks={[task]}
                iconButtonType
                disabled={Boolean(task.status_category_id && task.status_category_id > 2)}
              />
            )}

            <Typography sx={disableUpdateDates ? { textAlign: 'center' } : {}}>
              {followupDate}
            </Typography>
          </Box>
        </TableCell>
      ) : (
        <TableCell align="left" {...qaIdProp('completed_dt')}>
          <Box display="flex" alignItems="center">
            {!disableUpdateDates && <CompletedDateDialogForm tasks={[task]} iconButtonType />}

            <Typography sx={disableUpdateDates ? { textAlign: 'center' } : {}}>
              {completedDate}
            </Typography>
          </Box>
        </TableCell>
      )}
      {version === 'active' && (
        <TableCell align="left" {...qaIdProp('needsby_date')}>
          {showNBD && (
            <Box display="flex" alignItems="center">
              {!disableUpdateDates && !isInMedSync && (
                <NeedsByDateDialogForm
                  therapy={therapy}
                  disabled={Boolean(task.status_category_id && task.status_category_id > 2)}
                  task={task}
                  iconButtonType
                  initialDate={
                    task?.taskType === 'FDC' || task?.taskType === 'Order'
                      ? task?.due_date
                        ? moment.utc(task.due_date)
                        : null
                      : needsByDateUTC
                      ? moment.utc(needsByDateUTC)
                      : null
                  }
                />
              )}
              {isInMedSync ? (
                <Tooltip
                  title="Therapy is part of med sync, change the Anchor Date to move the NBD"
                  placement="top"
                >
                  <Typography sx={{ paddingLeft: '30px', cursor: 'help' }}>
                    {needsByDate}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography sx={disableUpdateDates ? { paddingLeft: '30px' } : {}}>
                  {needsByDate}
                </Typography>
              )}
              {!!isPerProtocol &&
              needsByDate &&
              (task.taskType === TaskTypesEnum.ORDER || task.taskType === TaskTypesEnum.FC) ? (
                <PerProtocolIcon />
              ) : null}
            </Box>
          )}
        </TableCell>
      )}

      <TableCell align="left" {...qaIdProp('actions')}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Tooltip
            title={patientId && patientFullName ? 'Create a new patient contact note' : 'Loading'}
          >
            <span style={{ display: 'flex' }}>
              <Communication
                disabled={!patientId || !patientFullName}
                tagTypeId={contactTagTypeId}
                task={task}
                tagResourceId={contactTagResourceId}
                buttonOverride={<CommunicationIcon />}
                selectedPatientId={patientId}
                tagName={patientFullName}
              />
            </span>
          </Tooltip>
          <Tooltip title={patientId ? 'Create a new intervention' : 'Loading'}>
            <span style={{ display: 'flex' }}>
              <Intervention
                disabled={!patientId}
                therapy={null}
                tagTypeId={tagTypeId}
                tagResourceId={patientId}
                serviceGroups={serviceGroups}
                taskTherapyId={task.therapy_id || null}
                taskType="patient"
                inheritedClass={actionIconStyling}
                buttonOverride={<InterventionIcon fontSize="small" />}
              />
            </span>
          </Tooltip>

          <Tooltip title={patientId ? 'Create a new QRE' : 'Loading'}>
            <span style={{ display: 'flex' }}>
              <Incident
                disabled={!patientId}
                therapy={therapy}
                tagTypeId={tagTypeId}
                tagResourceId={patientId}
                serviceGroups={serviceGroups}
                taskType="patient"
                {...(task.taskType === 'Order' ? { therapy_ids: task.therapy_ids } : {})}
                {...(task.taskType === 'Order' ? { fc_ids: task.fc_ids } : {})}
                buttonOverride={<IncidentIcon fontSize="small" className={actionIconStyling} />}
              />
            </span>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell align="center" {...qaIdProp('notes')}>
        {displayNotes?.length > 0 ? (
          <NoteTooltip title={<NoteTooltipContent displayNotes={displayNotes} />}>
            <IconButton className={classes.noteColumn} onClick={handleOnShowNotes} size="small">
              {noteCount ? <NoteContent displayNotes={displayNotes} /> : <AddIcon />}
            </IconButton>
          </NoteTooltip>
        ) : (
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            className={classes.emptyNoteColumn}
          >
            <IconButton onClick={handleOnShowNotes} size="small">
              <AddIcon />
            </IconButton>
          </Grid>
        )}
      </TableCell>
      <TableCell align="center" {...qaIdProp('documents')}>
        <IconButton onClick={handleOnShowDocuments} size="small">
          {taskDocuments?.length > 0 ? (
            <Typography>{taskDocuments?.length}</Typography>
          ) : (
            <AddIcon />
          )}
        </IconButton>
      </TableCell>
      {version === 'archived' && <TableCell align="center" />}
    </TableRow>
  );
  // #endregion renderers
};

const StyledTaskRow = withStyles(styles)(TasksTableRow);
export default StyledTaskRow;
